import { type RefObject, useCallback, useMemo, useRef, useState } from 'react';
import cn from 'classnames';
import { type OpenApiSchemaDashboardTodolistItem } from '../../api/schema';
import { withinLastMs } from '../../lib/datetime';
import { type DashboardTodoListItemProps } from './DashboardTodoListItem.types';
import styles from './DashboardTodoListItem.module.css';

export type DashboardTodoListItemGallerySlide = {
    id: string;
    index: number;
    src: string;
};

export type DashboardTodoListItemViewModel = {
    rootRef: RefObject<HTMLDivElement | null>;
    rootClassName: string;
    onRootClick: (() => void) | undefined;
    dataTestId: string | undefined;
    item: OpenApiSchemaDashboardTodolistItem;
    isExpanded: boolean;
    toggle: () => void;
    isCompleteUpdating: boolean;
    galleryItems: DashboardTodoListItemGallerySlide[];
    showNewLabel: boolean;
    featureHints: DashboardTodoListItemProps['featureHints'];
};

export function useDashboardTodoListItem({
    item,
    defaultExpanded = false,
    expanded,
    onExpandToggle,
    completingItemId = null,
    featureHints,
    'data-testid': dataTestId,
}: DashboardTodoListItemProps): DashboardTodoListItemViewModel {
    const [internalExpanded, setInternalExpanded] = useState(defaultExpanded);
    const isExpanded = expanded ?? internalExpanded;

    const rootRef = useRef<HTMLDivElement>(null);

    const toggle = useCallback(() => {
        const nextExpanded = !isExpanded;

        if (expanded === undefined) {
            setInternalExpanded(nextExpanded);
        }

        onExpandToggle?.(nextExpanded);
    }, [expanded, isExpanded, onExpandToggle]);

    const isCompleteUpdating = completingItemId === item.id;

    const galleryItems = useMemo<DashboardTodoListItemGallerySlide[]>(
        () =>
            item.images.map((src, index) => {
                return { id: `${item.id}-img-${index}`, index, src };
            }),
        [item.id, item.images]
    );

    const showNewLabel = !item.completed_at && withinLastMs(item.created_at);

    const rootClassName = cn(
        styles.root,
        isExpanded && styles.rootExpanded,
        isExpanded && item.completed_at && styles.rootExpandedCompleted
    );

    return {
        rootRef,
        rootClassName,
        onRootClick: isExpanded ? undefined : toggle,
        dataTestId: dataTestId,
        item,
        isExpanded,
        toggle,
        isCompleteUpdating,
        galleryItems,
        showNewLabel,
        featureHints,
    };
}
